import React, { useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Box, Button } from "@material-ui/core";
import { ExpandMore, ExpandLess } from "@material-ui/icons";
import { useTranslation } from "../hooks/useTranslation";
import FileDownload from "./FileDownload";
import { isAudioFile, isVideoFile } from "./MediaPlayer";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(0.25),
    minWidth: 180,
    maxWidth: 320,
  },
  section: {
    marginBottom: theme.spacing(1),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
    flexWrap: "wrap",
  },
  sectionDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  sectionDotPetition: {
    backgroundColor: "var(--ikaro-orange-primary)",
  },
  sectionDotSubsidies: {
    backgroundColor: "var(--ikaro-purple-accent)",
  },
  sectionTitle: {
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    color: "var(--ikaro-text-secondary)",
  },
  sectionCount: {
    fontSize: "0.7rem",
    color: "var(--ikaro-text-muted)",
    fontWeight: 500,
  },
  itemList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.25),
    paddingLeft: theme.spacing(1.5),
    borderLeft: "2px solid var(--ikaro-border-light)",
  },
  emptyState: {
    fontSize: "0.72rem",
    color: "var(--ikaro-text-muted)",
    fontStyle: "italic",
    paddingLeft: theme.spacing(1.5),
  },
  toggleButton: {
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(1.5),
    padding: theme.spacing(0.25, 0.75),
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "var(--ikaro-orange-primary)",
    textTransform: "none",
    minWidth: 0,
    lineHeight: 1.2,
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-light)",
    },
    "& .MuiButton-startIcon": {
      marginRight: theme.spacing(0.25),
    },
    "& .MuiButton-startIcon .MuiSvgIcon-root": {
      fontSize: "1rem",
    },
  },
}));

/* Quantos arquivos mostrar antes de pedir "Ver mais". Aplicado por seção;
   manter baixo para preservar a densidade da tabela quando há muitos itens. */
const MAX_VISIBLE_ITEMS = 3;

const categorize = (filename) => {
  if (isVideoFile(filename)) return "video";
  if (isAudioFile(filename)) return "audio";
  return "document";
};

/* `forceExpanded` exibe sempre todos os arquivos e oculta o botão "ver mais".
   Útil em contextos com espaço sobrando, como o drawer lateral de detalhes
   do job, onde não há motivo para limitar a quantidade de itens visíveis. */
const ContestacaoInputDisplay = ({ inputPayload, forceExpanded = false }) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const [expanded, setExpanded] = useState(false);
  const isExpanded = forceExpanded || expanded;

  const allSubsidios = useMemo(() => {
    const docs = inputPayload?.files?.subsidios;
    const av =
      inputPayload?.subsidios_audio_video ??
      inputPayload?.files?.subsidios_audio_video;
    return [
      ...(Array.isArray(docs) ? docs : []),
      ...(Array.isArray(av) ? av : []),
    ];
  }, [inputPayload]);

  /* Conta arquivos por tipo (documentos / áudio / vídeo) para o rótulo
     da seção. Útil porque cada tipo passa por um pipeline diferente. */
  const counts = useMemo(() => {
    const acc = { document: 0, audio: 0, video: 0 };
    for (const f of allSubsidios) {
      acc[categorize(f?.filename)] += 1;
    }
    return acc;
  }, [allSubsidios]);

  if (!inputPayload || !inputPayload.files) {
    return (
      <Box className={classes.container}>
        <Typography className={classes.emptyState}>
          {t("contestacao.inputDisplay.noInputFiles")}
        </Typography>
      </Box>
    );
  }

  const { files } = inputPayload;
  const hasMore = !forceExpanded && allSubsidios.length > MAX_VISIBLE_ITEMS;
  const visibleSubsidios = isExpanded
    ? allSubsidios
    : allSubsidios.slice(0, MAX_VISIBLE_ITEMS);
  const hiddenCount = Math.max(0, allSubsidios.length - MAX_VISIBLE_ITEMS);

  const countParts = [];
  if (counts.document > 0) {
    countParts.push(
      t("contestacao.inputDisplay.documents", {
        count: counts.document,
      }),
    );
  }
  if (counts.audio > 0) {
    countParts.push(
      t("contestacao.inputDisplay.audios", { count: counts.audio }),
    );
  }
  if (counts.video > 0) {
    countParts.push(
      t("contestacao.inputDisplay.videos", { count: counts.video }),
    );
  }
  const subsidiosCountLabel = countParts.join(" · ");

  return (
    <Box className={classes.container}>
      {/* Petição Inicial */}
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <span className={`${classes.sectionDot} ${classes.sectionDotPetition}`} />
          <Typography component="span" className={classes.sectionTitle}>
            {t("contestacaoCreate.initialPetition").replace(/:$/, "")}
          </Typography>
        </Box>
        {files.peticao_inicial ? (
          <Box className={classes.itemList}>
            <FileDownload
              file={files.peticao_inicial}
              html_preview_token={files.peticao_inicial.html_preview_token}
            />
          </Box>
        ) : (
          <Typography className={classes.emptyState}>
            {t("contestacao.inputDisplay.noFiles")}
          </Typography>
        )}
      </Box>

      {/* Subsídios */}
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <span className={`${classes.sectionDot} ${classes.sectionDotSubsidies}`} />
          <Typography component="span" className={classes.sectionTitle}>
            {t("contestacaoCreate.subsidies").replace(/:$/, "")}
          </Typography>
          {subsidiosCountLabel && (
            <Typography component="span" className={classes.sectionCount}>
              {subsidiosCountLabel}
            </Typography>
          )}
        </Box>
        {allSubsidios.length > 0 ? (
          <>
            <Box className={classes.itemList}>
              {visibleSubsidios.map((file, index) => (
                <FileDownload
                  key={`subsidio-${file.token || file.filename || index}`}
                  file={file}
                  html_preview_token={file.html_preview_token}
                />
              ))}
            </Box>
            {hasMore && (
              <Button
                size="small"
                disableRipple
                className={classes.toggleButton}
                onClick={() => setExpanded((v) => !v)}
                startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
              >
                {expanded
                  ? t("contestacao.inputDisplay.showLess")
                  : t("contestacao.inputDisplay.showMore", {
                      count: hiddenCount,
                    })}
              </Button>
            )}
          </>
        ) : (
          <Typography className={classes.emptyState}>
            {t("contestacao.inputDisplay.noFiles")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ContestacaoInputDisplay;
